/**
 * The counter seam rule at a run's inside corners (the user, 2026-11-03).
 *
 * Each leg's slab overhangs its cabinets by the profile's front overhang
 * (1"), so two slabs that each stop where their cabinets stop cross at an
 * inside corner:
 *
 *   - at an ANGLED corner they lie on each other by a sliver, and the kite's
 *     slab, built to the cabinets' fronts, stops an inch short of the
 *     counter's: the front edge steps;
 *   - at a SQUARE corner the leg whose slab does not run over the corner box
 *     starts at the box's edge — the other leg's CABINET front — and lies on
 *     that leg's front overhang for its whole depth.
 *
 * So at an angled corner each slab ends where the two slabs' fronts cross
 * (`buildCornerFillCountertop.slabSeams`) and the kite is rebuilt with the
 * legs' measured reach (`corners.buildObliqueKite`'s `slab`); at a square
 * corner the slab that does not reach the vertex starts at the other's front.
 * Either way the front edge comes to one point and no two pieces stack. The
 * scan pass keeps the same rule at every oblique junction
 * (`oblique-junction-finish.js`); the cut itself is `slabSeam.cutSlabTo`.
 *
 * `fillRun` applies it to every corner it builds, once its countertops exist;
 * a drawn island leg is finished after that (`bentRunFill.finishBentLegs`
 * grows its slab to the island's outer edge), so the bend step measures again
 * and rebuilds the kite with the leg's final back (`seamObliqueCorner`, with
 * the stops `fillRun` returns).
 *
 * Pure. No I/O.
 */

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { slabSeams } = require('../kitchen-assembly/buildCornerFillCountertop');
const { planOutline } = require('../geometry/objectFootprint');
const { bendAt, legDirection } = require('./bendGeometry');
const { buildObliqueKite } = require('./corners');
const { alongLeg, cutSlabTo, EPS_M } = require('./slabSeam');
const { segmentCornerDepth } = require('./runDepth');

const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const mul = (a, s) => ({ x: a.x * s, z: a.z * s });
const dot = (a, b) => a.x * b.x + a.z * b.z;

/**
 * A leg's own slabs: `buildCountertopRuns` names each after the wall it
 * stands on, and a drawn island leg's carry its stored index once the island
 * fill has finished them.
 */
const isCounterOnLeg = (c, seg) =>
  c?.type === 'countertop' &&
  typeof c.id === 'string' &&
  (c.id.startsWith(`countertop_${seg.wall.id}_`) ||
    (Boolean(seg.wall?.drawnFreeLeg) && c.freeSegIndex === seg._storedIdx));

/**
 * One leg as it meets a corner: the direction AWAY from the vertex along it,
 * its cabinet-side normal, how far its slabs reach behind its line (`back` —
 * 0 on a real wall, whose back is the wall) and in front of it (`front`), and
 * where along it, from the vertex, its slabs begin (`reach`).
 */
function legAtCorner(seg, vertex, away, counters) {
  const n = getWallAxes(seg.wall)?.wallNormal;
  if (!n) return null;
  let back = 0;
  let front = 0;
  let reach = Infinity;
  for (const ct of counters) {
    const poly = planOutline(ct);
    if (!poly) continue;
    const across = poly.map((p) => dot(sub(p, vertex), n));
    const along = poly.map((p) => dot(sub(p, vertex), away));
    back = Math.max(back, -Math.min(...across));
    front = Math.max(front, Math.max(...across));
    reach = Math.min(reach, Math.min(...along));
  }
  return {
    away,
    n,
    back: seg.wall?.isVirtual || seg.wall?.drawnFreeLeg ? back : 0,
    front,
    reach: Number.isFinite(reach) ? Math.max(0, reach) : 0,
  };
}

/**
 * The two legs at the corner between `a` and `b` (in run order): the vertex,
 * each leg's slabs, and each leg's own description there.
 */
function legsAtCorner(a, b, counters) {
  const bend = bendAt(a, b);
  const dA = legDirection(a);
  const dB = legDirection(b);
  if (!bend || !dA || !dB) return null;
  const V = bend.vertex;
  const cA = counters.filter((c) => isCounterOnLeg(c, a));
  const cB = counters.filter((c) => isCounterOnLeg(c, b));
  return {
    V,
    cA,
    cB,
    A: legAtCorner(a, V, mul(dA, -1), cA),
    B: legAtCorner(b, V, dB, cB),
  };
}

/**
 * Cut every slab in `counters` lying along `leg` back to `seam` from V, and
 * record where (`seamFrom`): the late pass that squares a slab back onto its
 * cabinets (`post-processing/kitchen.js` `resyncCountertopsToCabinets`)
 * honours the cut while what it gave up is still under another countertop,
 * as it honours a scribe (`scribeTo`).
 */
function cutLegSlabs(counters, V, leg, seam, into) {
  const seamFrom = {
    x: V.x + leg.away.x * seam,
    z: V.z + leg.away.z * seam,
  };
  for (const ct of counters) {
    if (!ct.dimensions || !alongLeg(ct, leg.away)) continue;
    const { slab, cut } = cutSlabTo(ct, V, leg.away, seam);
    if (cut) into.set(ct.id, { ...slab, seamFrom });
  }
}

/**
 * A leg with no slab of its own at the corner — the stretch past the kite is
 * a doorway, its sliver left empty (`strandedFillerIndices`) — still has a
 * front for the kite's slab to meet: where a slab on it would stand, the
 * kite's own corner-box depth plus the run's front overhang. Null when the
 * caller did not say what the overhang is.
 */
function emptyLeg(leg, seg, ctx, frontOverhang) {
  if (!(frontOverhang >= 0)) return null;
  const depth = segmentCornerDepth(seg, ctx);
  if (!(depth > 0)) return null;
  return { ...leg, front: depth + frontOverhang, back: 0 };
}

/**
 * At an angled corner with a kite, end each leg's slab where the two slabs'
 * fronts cross and rebuild the kite's slab with the legs' reach, so its
 * front comes to the crossing and its back meets a virtual leg's back
 * overhang. `stopA` / `stopB` are the legs' cabinet stops the kite was built
 * at. A leg with no slab takes the front a slab on it would have
 * (`emptyLeg`), so the kite's front still overhangs its cabinets as the
 * counter's does everywhere else. Unchanged where the corner has no kite
 * slab or the fronts do not cross past both stops.
 *
 * @returns {Object[]} `objects` with the kite slab and the legs' slabs
 *   replaced
 */
function seamObliqueCorner({
  run,
  segments,
  cornerIndex,
  stopA,
  stopB,
  objects,
  floorY,
  ctx,
  frontOverhang,
}) {
  const piece = objects.find(
    (o) =>
      o?.type === 'corner-fill-countertop' &&
      o.runId === run.id &&
      o.cornerIndex === cornerIndex
  );
  if (!piece || !ctx || !(stopA > EPS_M) || !(stopB > EPS_M)) return objects;
  const legs = legsAtCorner(
    segments[cornerIndex],
    segments[cornerIndex + 1],
    objects
  );
  if (!legs?.A || !legs?.B) return objects;
  const { V } = legs;
  const A = legs.cA.length
    ? legs.A
    : emptyLeg(legs.A, segments[cornerIndex], ctx, frontOverhang);
  const B = legs.cB.length
    ? legs.B
    : emptyLeg(legs.B, segments[cornerIndex + 1], ctx, frontOverhang);
  if (!A || !B) return objects;
  const seams = slabSeams({
    nA: A.n,
    dA: A.away,
    frontA: A.front,
    stopA,
    nB: B.n,
    dB: B.away,
    frontB: B.front,
    stopB,
  });
  if (!seams) return objects;
  const rebuilt = buildObliqueKite({
    run: { ...run, segments },
    cornerIndex,
    stopA,
    stopB,
    floorY,
    ctx,
    slab: {
      A: { front: A.front, back: A.back },
      B: { front: B.front, back: B.back },
    },
  })?.countertop;
  if (!rebuilt?.polygonCorners) return objects;
  const cut = new Map();
  cutLegSlabs(legs.cA, V, A, seams.seamA, cut);
  cutLegSlabs(legs.cB, V, B, seams.seamB, cut);
  return objects.map((o) =>
    o === piece
      ? {
          ...piece,
          center: rebuilt.center,
          dimensions: rebuilt.dimensions,
          polygonCorners: rebuilt.polygonCorners,
        }
      : cut.get(o.id) || o
  );
}

/**
 * At a square corner one leg's slab runs over the corner box to the vertex
 * and the other's starts at the box's edge — the first leg's cabinet front —
 * an overhang short of that slab's own front. The second slab starts at the
 * first's front instead. Unchanged where neither or both reach the vertex.
 */
function seamSquareCorner({ segments, cornerIndex, objects }) {
  const legs = legsAtCorner(
    segments[cornerIndex],
    segments[cornerIndex + 1],
    objects
  );
  if (!legs?.A || !legs?.B || !legs.cA.length || !legs.cB.length) {
    return objects;
  }
  const { V, A, B } = legs;
  const reachesA = A.reach <= EPS_M;
  const reachesB = B.reach <= EPS_M;
  if (reachesA === reachesB) return objects;
  const [through, other, otherSlabs] = reachesA
    ? [A, B, legs.cB]
    : [B, A, legs.cA];
  // the through slab's front line, measured along the other leg
  const across = dot(other.away, through.n);
  if (!(across > EPS_M)) return objects;
  const seam = through.front / across;
  const cut = new Map();
  cutLegSlabs(otherSlabs, V, other, seam, cut);
  return cut.size ? objects.map((o) => cut.get(o.id) || o) : objects;
}

/**
 * Every inside corner of a run, after `fillRun` has built its countertops.
 *
 * @param {{ run, countertops: Object[], oblique: Array<{cornerIndex,
 *   stopA, stopB}>, squareCorners: number[], floorY: number, ctx,
 *   frontOverhang?: number }} a
 *   `oblique` — the angled corners and their stops; `squareCorners` — the
 *   indices of the square ones (a corner unit, a blind corner, an empty box);
 *   `frontOverhang` — how far the run's slabs stand proud of its cabinets,
 *   for a corner leg that has no slab to measure
 * @returns {Object[]} the countertops, seamed
 */
function seamCornerSlabs({
  run,
  countertops,
  oblique,
  squareCorners,
  floorY,
  ctx,
  frontOverhang,
}) {
  let out = countertops;
  for (const { cornerIndex, stopA, stopB } of oblique) {
    out = seamObliqueCorner({
      run,
      segments: run.segments,
      cornerIndex,
      stopA,
      stopB,
      objects: out,
      floorY,
      ctx,
      frontOverhang,
    });
  }
  for (const cornerIndex of squareCorners) {
    out = seamSquareCorner({
      segments: run.segments,
      cornerIndex,
      objects: out,
    });
  }
  return out;
}

module.exports = {
  isCounterOnLeg,
  legsAtCorner,
  seamObliqueCorner,
  seamSquareCorner,
  seamCornerSlabs,
};
